import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSelector from "@/components/ui/language-selector";
import { AuthModal } from "@/components/auth/AuthModal";
import { LogOut } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSignOut } from "@/hooks/useSignOut";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleSignOut } = useSignOut();

  const navItems = [
    { name: t('navigation.home'), href: '#home' },
    { name: t('navigation.about'), href: '#about' },
    // { name: t('navigation.programs'), href: '#programs' },
    { name: t('navigation.faq'), href: '#faq' },
  ];

  const scrollToSection = (href: string) => {
    // If we're not on the home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // We're on the home page, just scroll
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false);
  };

  const openLogin = () => {
    if (user) {
      // User is already logged in, redirect based on role
      if (profile?.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } else {
      // User is not logged in, show auth modal
      setShowAuthModal(true);
    }
    setIsOpen(false);
  };

  const handleLogout = async () => {
    await handleSignOut();
    setIsOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          
          {/* Logo */}
          <div className="font-black text-2xl gradient-text">
            {t('navigation.logo')}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => scrollToSection(item.href)}
                className="text-foreground hover:text-primary transition-colors font-medium"
              >
                {item.name}
              </button>
            ))}
            <LanguageSelector />
          </div>

          {/* CTA Buttons (Login/Dashboard/Admin/Logout) */}
          <div className="hidden md:flex items-center space-x-3">
            {user ? (
              <>
                <Button 
                  onClick={openLogin} 
                  className="hero-button"
                  disabled={profileLoading}
                >
                  {profileLoading ? 'Loading...' : (profile?.role === 'admin' ? 'ADMIN' : 'DASHBOARD')}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleLogout}
                  className="text-foreground hover:text-primary border-foreground/20 hover:border-primary/50"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </>
            ) : (
              null
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex flex-col justify-center space-y-1">
              <span className={`block h-0.5 w-6 bg-foreground transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
              <span className={`block h-0.5 w-6 bg-foreground transition-all duration-300 ${isOpen ? 'opacity-0' : ''}`}></span>
              <span className={`block h-0.5 w-6 bg-foreground transition-all duration-300 ${isOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-background/95 backdrop-blur-md border-t border-border/50">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => scrollToSection(item.href)}
                  className="block px-3 py-2 text-foreground hover:text-primary transition-colors font-medium w-full text-left"
                >
                  {item.name}
                </button>
              ))}
              <div className="px-3 py-2">
                <LanguageSelector />
              </div>
              <div className="px-3 py-2 space-y-2">
                {user ? (
                  <>
                    <Button 
                      onClick={openLogin} 
                      className="hero-button w-full"
                      disabled={profileLoading}
                    >
                      {profileLoading ? 'Loading...' : (profile?.role === 'admin' ? 'ADMIN' : 'DASHBOARD')}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleLogout}
                      className="w-full text-foreground hover:text-primary border-foreground/20 hover:border-primary/50"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </Button>
                  </>
                ) : (
                  null
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode="login"
      />
    </nav>
  );
};

export default Navigation;